import {
  Callout,
  Figure,
  H2,
  H3,
  LI,
  P,
  Table,
  UL,
} from "../../components/blog/BlogTypography";

import cisoHonestyChart from "../../assets/blog/agentic-soc-guide/ciso-honesty-chart.png";
import fiveStageMaturity from "../../assets/blog/agentic-soc-guide/five-stage-maturity.png";
import spendImbalanceChart from "../../assets/blog/agentic-soc-guide/spend-imbalance-chart.png";
import ninetyDayPlan from "../../assets/blog/agentic-soc-guide/ninety-day-plan.png";

const TIMELINE_HEADERS = ["Org size", "Total time", "Scoping", "Integration", "Shadow pilot", "Cutover"];
const TIMELINE_ROWS = [
  ["SMB (under 500 people)", "2-4 weeks", "2-3 days", "1-1.5 weeks", "3-5 days", "1-2 days"],
  ["Mid-market (500-5,000)", "4-6 weeks", "1 week", "2-3 weeks", "1-2 weeks", "2-3 days"],
  ["Enterprise (5,000+)", "6-10 weeks", "1-2 weeks", "3-4 weeks", "2-3 weeks", "1 week"],
];

const VENDOR_HEADERS = ["Platform", "What it is"];
const VENDOR_ROWS = [
  [
    "Microsoft Security Copilot",
    "A GPT-based assistant tied closely to the Microsoft security stack. Mostly prompt-driven today, not fully autonomous.",
  ],
  [
    "CrowdStrike Charlotte AI / Falcon",
    "Agentic features layered on top of Falcon's endpoint telemetry, expanding into agent orchestration under the Charlotte name.",
  ],
  [
    "Palo Alto Networks Cortex XSIAM",
    "A combined detection, response, and SIEM platform. Powerful, but a heavy lift to implement well.",
  ],
  [
    "Splunk SOAR",
    "Mature, broadly integrated playbook automation. Still needs sustained engineering time to maintain.",
  ],
  [
    "Dropzone AI, Prophet Security, Conifers.ai, Intezer, Torq",
    "Newer companies built agentic from the start. Some deploy in days with little configuration; others go deep on forensic investigation.",
  ],
];

export default function AgenticSocGuide() {
  return (
    <>
      <Callout>
        Analysis and guidance for security leaders evaluating AI-driven SOC deployments, synthesized
        from Gartner, Splunk, Okta, CrowdStrike, EY, NIST, and vendor research cited throughout.
        Figures reflect third-party surveys and vendor-reported case studies unless noted otherwise.
      </Callout>

      <P>
        Somewhere right now, an attacker is inside a network they were never invited into. In 2025,
        the average time between that first foothold and the moment they start moving sideways,
        hunting for something valuable, dropped to 29 minutes. The fastest recorded case took 27
        seconds. Not minutes. Seconds.
      </P>
      <P>
        No SOC built around people reading alerts one at a time can keep pace with that. This is the
        plain fact sitting behind every conversation happening in security right now about
        artificial intelligence, and it's why boards that used to treat AI security as a line in the
        annual budget review are asking sharper questions this year.
      </P>
      <P>
        What follows is an attempt to answer those questions honestly: what's actually working,
        what's still mostly marketing, and what a CXO needs to do, roughly in order, to get real
        value out of AI in the SOC without quietly creating a new problem in its place.
      </P>

      <H2>Two stories, and only one of them is true everywhere</H2>
      <P>
        Ask a vendor about AI in the SOC and you'll hear one story: agents that triage alerts, draft
        incident reports, and hunt threats around the clock without waiting on a tired analyst at 2
        a.m. Gartner has a name for where that story currently sits on its hype curve, the Peak of
        Inflated Expectations, the stage where visibility is highest and the gap between promise and
        proof is widest. Real deployment, by Gartner's own estimate, still sits at just 1-5% of
        organizations.
      </P>
      <P>
        Ask a working CISO instead and you get a second, quieter story. Splunk surveyed 650 of them
        in mid-2025 and found genuine enthusiasm wherever AI is actually running: 92 percent say it
        lets them review far more security events than before, and teams running agentic AI in
        production report detection and response speeds more than twice as fast as teams still just
        experimenting.
      </P>
      <P>
        But ask those same CISOs a harder question, do you actually know what your AI agents are
        doing, and the story shifts again. Okta's 2026 survey of 306 security leaders found that
        fewer than half can say, with real confidence, that they know every AI agent running in
        their environment, control what those agents can access, or approve the individual actions
        those agents take.
      </P>

      <Figure
        src={cisoHonestyChart}
        alt="Bar chart titled 'What CISOs can honestly say about their own AI agents': 47% know every AI agent running in their environment, 46% control what those agents can access, 45% approve each individual action an agent takes. Source: Okta, Global CISO Insights 2026, 306 security leaders surveyed."
        caption="Source: Okta, Global CISO Insights 2026 (306 security leaders surveyed)."
      />

      <P>
        An AI agent with access to your tools isn't a feature. It's an identity, with permissions and
        a blast radius, and most organizations still aren't treating it like one. Gartner's advice to
        buyers is blunt: if a vendor can't show you exactly how a decision was made and controlled,
        don't call it an autonomous agent. Call it what it is, an unverified workflow, and budget
        accordingly.
      </P>

      <H2>Where does your own SOC actually sit?</H2>
      <P>
        Most conversations about AI maturity happen in the abstract. It helps to make it concrete.
        Security teams tend to move through five recognizable stages, and most mid-market
        organizations today sit somewhere between the second and the third.
      </P>

      <Figure
        src={fiveStageMaturity}
        alt="Bar chart titled 'Five stages every SOC passes through', showing the share of alerts handled without a human touching them first: Level 1 Manual under 10%, Level 2 Playbook-driven 20-40%, Level 3 AI-assisted 40-60%, Level 4 Agentic 70-85%, Level 5 Autonomous 85-95%. Adapted from maturity frameworks published by Underdefense and CrowdStrike; figures are directional."
        caption="Adapted from maturity frameworks published by Underdefense and CrowdStrike; figures are directional."
      />

      <P>
        At Level 1, a person reads every alert and decides what to do by hand, and it isn't unusual
        for a real threat to sit undetected for more than a day. By Level 3, a copilot suggests the
        next step and an analyst approves it, which removes a lot of the busywork but still keeps
        someone in every loop. It's only from Level 4 onward that agents start acting on their own
        for the low-risk, high-volume work, saving human judgment for the calls that actually need
        it.
      </P>
      <P>
        One modeled case, a mid-market deployment covering 1,000 endpoints and a five-person team,
        put a number on what that shift is worth: alert triage time falling from roughly 45 minutes
        to under two, false positives dropping from the 60 to 80 percent range to under 10 percent,
        and a modeled net return of 539 percent once implementation costs were subtracted. Numbers
        like that deserve a healthy dose of skepticism, they come from a vendor case study, not an
        audited outcome, but even a fraction of that return, delivered safely, changes the economics
        of running a SOC.
      </P>
      <P>
        How long does this actually take? It depends almost entirely on how many tools you already
        run and how many people need to sign off, not on which vendor you pick. A pattern that holds
        fairly consistently: organizations with one or two SIEM or EDR tools and a short chain of
        decision-makers can get through scoping, integration, a shadow-mode pilot, and production
        cutover in two to four weeks. Mid-sized organizations juggling three to five tools and formal
        compliance mapping tend to need four to six. Large, multi-region enterprises running more
        than one SIEM, with change management across shift patterns and several regulatory
        jurisdictions, are realistically looking at six to ten weeks for a focused rollout.
      </P>

      <Table headers={TIMELINE_HEADERS} rows={TIMELINE_ROWS} />

      <H2>Six things worth getting right, roughly in this order</H2>

      <H3>1. Treat every agent like a new hire with admin rights</H3>
      <P>
        Because that's effectively what it is. Before scaling anything, map your program against
        NIST's AI Risk Management Framework: govern, decide who owns this at the top; map, find where
        AI actually touches your infrastructure and data; measure, work out how likely and how bad
        the failure modes are; and manage, put technical and procedural controls in place that catch
        problems when they happen. Then put AI risk on the board's agenda directly, in plain business
        terms. Right now only 46 percent of CISOs believe their board even sees AI security as
        something that helps the business, rather than a compliance cost to tolerate.
      </P>
      <P>
        There's a practical reason to take this seriously beyond risk reduction. Five controls do
        most of the work: human sign-off on any critical action, a full audit trail of every step the
        AI takes, reasoning an analyst can actually follow rather than a black-box verdict,
        role-based access over who can configure or override the AI, and regular testing against
        simulated attacks. Those same five controls map almost directly onto the access, monitoring,
        and testing requirements already sitting inside SOC 2, ISO 27001, HIPAA, and NIS2. Build them
        once, properly, and you get a safer AI SOC and audit-ready evidence for frameworks you're
        already on the hook for.
      </P>

      <H3>2. Fix your data before you fix your process</H3>
      <P>
        An agent is only as good as what it can see. Before any serious rollout, consolidate your
        logging, enrich it with real asset context (what actually matters if it's compromised), and
        feed it current threat intelligence. Just as important, write down what your best analysts
        already carry in their heads, the shortcuts, the judgment calls, the "this alert always turns
        out to be nothing" instincts, so the agent inherits that expertise instead of reinventing it
        badly.
      </P>

      <H3>3. Redesign roles, don't just remove tasks</H3>
      <P>
        The honest version of "AI replaces Tier 1" is that Tier 1 work changes shape. Analysts freed
        from repetitive triage move into more senior work: refining detection logic, hunting threats,
        reviewing what the agents actually did. That's a good outcome, but only if it's planned and
        budgeted for. Two-thirds of security teams already report significant burnout; a role
        transition nobody prepared them for will make that worse, not better.
      </P>

      <H3>4. Spend real money securing the AI, not just buying more of it</H3>
      <P>Here's an uncomfortable number.</P>

      <Figure
        src={spendImbalanceChart}
        alt="Bar chart titled 'For every $1 spent securing AI, about $17 goes to buying AI-powered defenses': 2025 spend on buying AI to defend with was $49B, versus $2.8B on securing the AI itself, about 17x more. Source: Software Strategies Blog, Information Security Spending 2026, citing IDC-derived figures."
        caption="Source: Software Strategies Blog, Information Security Spending 2026, citing IDC-derived figures."
      />

      <P>
        For every dollar organizations spend securing their AI systems, they're spending roughly
        seventeen dollars buying AI to defend with. That imbalance is exactly where the next serious
        incident is likely to start. Agentic tools that can take action on your behalf are also
        unusually easy to manipulate: testing has found prompt injection succeeding against
        tool-using agents up to 84 percent of the time, far higher than against a simple chat
        interface. One successful injection can chain into stolen data, unauthorized commands, and
        lateral movement in a single sequence. The fix isn't exotic. Validate what goes into the
        model, make sure system instructions can't be overridden by whatever text the agent happens
        to ingest, grant the narrowest possible tool access with a human sign-off on anything
        sensitive, check what comes out for signs of leakage, and test the whole thing against known
        attack patterns on a regular schedule.
      </P>

      <H3>5. Match the guardrails to how much autonomy you're granting</H3>
      <P>
        The more independently an agent acts, the more you need to be able to reconstruct, after the
        fact, exactly why it did what it did. That means a bounded scope for what an agent can touch,
        version control on its logic and prompts, clear ownership of who can adjust its thresholds or
        approve its actions, and a cost cap so a runaway process doesn't become a runaway bill. Build
        this before scaling, not after something goes wrong.
      </P>
      <P>It helps to think of autonomy as a dial with three settings, not a switch.</P>
      <UL>
        <LI>
          <strong>Tier one</strong> is fully supervised, nothing happens without a human approving it
          first.
        </LI>
        <LI>
          <strong>Tier two</strong> is constrained autonomy, the agent can act on its own but only
          within a narrow, pre-approved set of action types.
        </LI>
        <LI>
          <strong>Tier three</strong> is broad autonomy, the agent acts freely within defined
          boundaries under continuous monitoring, with humans reviewing rather than approving each
          step.
        </LI>
      </UL>
      <P>
        Most organizations don't fail by picking the wrong tier. They fail by skipping the dial
        altogether and defaulting new agents straight to broad autonomy because that's the tier the
        demo was set to. Grant the lowest tier that actually gets the job done, and move an agent up
        a tier deliberately, never by default. Independent research on enterprise agent deployments
        suggests a large share of organizations, by some estimates as many as 4 in 5, don't have full
        visibility into every agent running in their environment, and that roughly half of enterprise
        AI usage happens through personal accounts that never touch single sign-on. Those two numbers
        alone explain most agent-related incidents. You can't govern what you can't see, which is why
        an inventory comes before any of the rest of this.
      </P>

      <H3>6. Report progress in numbers your board will actually believe</H3>
      <P>
        CISO liability concern is up sharply this year: more than three-quarters now say they
        personally worry about it, and nearly all of them carry some formal AI governance
        responsibility whether they asked for it or not. A short, consistent set of metrics goes a
        long way: detection and response time trends, alert triage time, false-positive rate, the
        share of actions taken autonomously versus with a human sign-off, and how current the
        agent-access reviews actually are. The same numbers, reported the same way every quarter, are
        what turn board skepticism into sustained budget.
      </P>

      <H2>A readiness check before you call anyone</H2>
      <P>
        Before the first vendor call, it's worth scoring your own organization honestly against ten
        questions. This isn't a sales qualification exercise, it's an operational planning tool, and
        being honest about the gaps now saves months later.
      </P>
      <UL>
        <LI>
          A documented inventory exists of every security tool and data source you run, not a mental
          list someone in IT could probably reconstruct.
        </LI>
        <LI>Current MTTD and MTTR are measured, not estimated from memory.</LI>
        <LI>An executive sponsor is identified and genuinely committed, not just named on a slide.</LI>
        <LI>A technical lead has five to ten hours a week free during onboarding.</LI>
        <LI>Your existing tools support API-based integration.</LI>
        <LI>
          You've mapped which compliance frameworks actually apply to you (SOC 2, ISO 27001, HIPAA,
          NIS2, or others specific to your industry).
        </LI>
        <LI>
          An analyst champion is designated to own change management on the floor, not just the
          rollout on paper.
        </LI>
        <LI>Incident response playbooks are actually written down, not just known by your most experienced person.</LI>
        <LI>Budget is approved, not informally discussed.</LI>
        <LI>API access and credentials are ready for the systems that need to connect.</LI>
      </UL>
      <P>
        Score one point for every yes. Eight to ten and you're ready to start evaluating vendors now.
        Five to seven and you need one to two weeks of prep work, mostly documentation you already
        have somewhere, it just needs pulling together. Below five, fix the gaps first. An executive
        sponsor and a real tool inventory matter more at this stage than which vendor you eventually
        choose. Even the best AI SOC deployment stalls in week one without them.
      </P>

      <H2>What could still go wrong</H2>
      <P>
        Even a well-governed rollout doesn't erase every risk. A few are worth watching specifically
        because they're new, or newly serious, in 2026.
      </P>
      <UL>
        <LI>
          Non-human identities are quietly becoming the weak link: only about 1 in 6.7 organizations
          feel confident securing their AI and service identities, against 1 in 4 for human ones, and
          gaps in credential rotation are the most cited cause of related incidents.
        </LI>
        <LI>
          CISOs now rank AI-generated phishing, rogue or manipulated agents, and deepfake-enabled
          authentication bypass among their top concerns (61, 49, and 45 percent respectively).
        </LI>
        <LI>
          The EchoLeak vulnerability in Microsoft Copilot (CVE-2025-32711) showed that a
          well-crafted piece of ingested content, with no click required from the victim, could
          quietly exfiltrate data through a legitimate AI assistant. Any SOC copilot that reads
          tickets, emails, or third-party threat feeds carries some version of that same exposure.
        </LI>
        <LI>
          68 percent of CISOs admit unsanctioned AI tools are already running somewhere in their
          organization, usually well ahead of any policy written to govern them.
        </LI>
        <LI>
          The same prompt-injection techniques that compromised developer tooling inside CI/CD
          pipelines apply just as well to a SOC toolchain built on external AI agents.
        </LI>
      </UL>
      <P>
        One more date worth knowing if you operate in or serve the EU. The AI Act's Article 12
        requires high-risk AI systems to automatically log events across their lifetime, and Article
        26 requires deployers to keep those logs for at least six months. Penalties for
        non-compliance reach fifteen million euros or three percent of global turnover, whichever is
        larger. Build tamper-evident logging into your AI SOC controls now and this becomes a
        non-issue later. Bolt it on after an audit finding and it becomes a far more expensive
        project.
      </P>

      <H2>Who's actually building this</H2>
      <P>
        The vendor market hasn't settled yet, and it's moving fast enough that any list goes stale
        within months. Broadly, it splits two ways: established platforms bolting agentic features
        onto tools you may already own, and newer companies built agentic from day one.
      </P>

      <Table headers={VENDOR_HEADERS} rows={VENDOR_ROWS} />
      <P>
        Judge any platform the way Gartner suggests, by whether it can show you how a decision was
        made, not by whether its marketing uses the word "autonomous."
      </P>

      <H2>How to actually evaluate a vendor</H2>
      <P>
        Most vendor evaluations go wrong the same way. Buyers get pulled in by AI capability
        marketing, the most advanced model, the most autonomous agents, the biggest training set,
        while skipping the operational questions that actually determine whether the deployment works
        six months in. A more reliable approach is to score each finalist, zero to two, on seven
        plain questions.
      </P>
      <UL>
        <LI>
          Does it work with what you already run, or does it quietly require replacing your SIEM or
          EDR before it delivers any value?
        </LI>
        <LI>
          How fast can it actually go live? Thirty days is achievable for a focused deployment. If
          the answer is a multi-month professional services engagement, ask why.
        </LI>
        <LI>
          When the AI can't resolve something, who do you actually reach? A ticket queue with a
          multi-hour response time is a very different product from direct access to a senior
          analyst.
        </LI>
        <LI>
          Can it contain a threat, or only describe one? Detection and notification are the easy
          part; ask exactly what it can do without a human clicking anything, and what it escalates
          instead.
        </LI>
        <LI>
          Can it verify a suspicious action with the person it actually affects, not just log it? A
          2 a.m. login from an unfamiliar location is far more useful triaged when the system can
          message the affected employee directly and get a yes or no.
        </LI>
        <LI>Is pricing public and predictable, or hidden behind a quote that changes after year one?</LI>
        <LI>Is compliance evidence generation built in, or a separate line item you'll need to buy again?</LI>
      </UL>
      <P>
        A vendor scoring twelve or higher out of fourteen is worth a serious evaluation. Below eight,
        you're likely buying an alert feed with an AI label on it, not a system that changes how your
        SOC actually operates.
      </P>

      <H2>A story in three steps: the next 90 days</H2>
      <P>None of this has to start big. A sensible first 90 days looks something like this.</P>

      <Figure
        src={ninetyDayPlan}
        alt="Three-step timeline: Days 1-15 Look and listen, Days 16-45 Try it quietly, Days 46-90 Go live, carefully."
        caption="A phased approach to AI SOC adoption over the first 90 days."
      />

      <P>
        In the first two weeks, the job is mostly listening: find every AI agent already running,
        sanctioned or not, write down your current numbers (detection time, response time, alert
        volume, how burned out the team already is), and get the board into one room to agree,
        explicitly, on how much AI risk the organization is willing to carry.
      </P>
      <P>
        Over the following month, pick one narrow, high-volume problem, phishing triage is the most
        common starting point, and run an agent alongside your analysts without letting it act on its
        own yet. Use that window to build the governance structure it will eventually operate inside:
        NIST-mapped approval gates, action logging, all of it.
      </P>
      <P>
        By day 90, move that pilot into production, but only let it act independently on the
        lower-risk calls. Start writing your best analysts' judgment into its operating context, and
        put together the first version of the metrics package for the board. None of this finishes at
        day 90. It just means you've started somewhere real instead of somewhere borrowed from a
        vendor slide.
      </P>

      <H2>Where this leaves you</H2>
      <P>
        The technology here is genuinely useful, in the places it has actually been proven. The
        problem was never the technology. It's that fewer than half of security leaders can currently
        say, honestly, that they know what their own AI agents are doing. Fix that first, and
        everything else in this blog becomes a lot more straightforward. Skip it, and 2026 may be
        remembered as the year a lot of organizations handed real operational authority to systems
        nobody had fully checked.
      </P>

      <Callout>
        <strong>Sources:</strong> Gartner, 2026 Security Operations Hype Cycle · EY, Agentic AI:
        Transforming Security Operations Centers · Splunk, agentic AI takes center stage in CISOs'
        path to digital resilience · Okta, Global CISO Insights 2026 · CrowdStrike, How AI-Leading
        Security Teams Are Building the Agentic SOC · Underdefense, AI SOC Automation in 2026 and The
        Complete AI SOC Implementation Guide for 2026 · Palo Alto Networks, NIST AI Risk Management
        Framework · Vectra AI, Prompt Injection: types, real-world CVEs, and enterprise defenses ·
        Sentra, EchoLeak (CVE-2025-32711) · Software Strategies Blog, Information Security Spending
        2026 · Conifers.ai, Top 10 AI SOC Agents, Platforms and Solutions in 2026 · Iternal.ai, AI
        Agent Security Checklist (2026).
      </Callout>
    </>
  );
}
